import {
  reportsQueryScopeHasFilters,
  type ReportsQueryScope,
} from '../../reports/domain/reportsQueryScope';
import { getSalesSnapshotByDateRangeWithVentasScope } from './salesRepoSnapshotsToday';
import { getSalesTimeBandWindows } from './salesTimeBandWindows';
export { getSalesTimeBandWindows };
export type { TimeBand, SalesByTimeBandRow, SalesComparison, SalesByCashierRow } from './salesSnapshotTypes';
import type { SalesByTimeBandRow, SalesComparison } from './salesSnapshotTypes';

/**
 * ADR-017 Fase 4: SQLite eliminado como path de datos operacional.
 * Snapshots/bands sin scope remoto retornan cero/vacío.
 * Con scope usan `getSalesSnapshotByDateRangeWithVentasScope` (Supabase).
 * Ranking por cajero: owner Informes (`fetchRemoteSalesByCashier`), no este módulo.
 */

async function getScopedSalesAgg(
  branchId: string,
  fromIso: string,
  toIso: string,
  scope?: ReportsQueryScope | null
): Promise<{ count: number; total: number }> {
  if (!scope || !reportsQueryScopeHasFilters(scope)) {
    return { count: 0, total: 0 };
  }
  const snap = await getSalesSnapshotByDateRangeWithVentasScope(branchId, fromIso, toIso, scope);
  return { count: snap.salesCount, total: snap.salesTotal };
}

export const getSalesByTimeBand = async (
  branchId: string,
  dateIso: string,
  scope?: ReportsQueryScope | null
): Promise<SalesByTimeBandRow[]> => {
  const bands = getSalesTimeBandWindows(dateIso);

  const results: SalesByTimeBandRow[] = [];
  for (const band of bands) {
    const agg = await getScopedSalesAgg(branchId, band.start, band.end, scope);
    results.push({
      band: band.key,
      label: band.label,
      count: agg.count,
      total: agg.total,
    });
  }
  return results;
};

export const getSalesComparison = async (
  branchId: string,
  scope?: ReportsQueryScope | null
): Promise<SalesComparison> => {
  const now = new Date();
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  const yesterdayStart = new Date(now);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  yesterdayStart.setHours(0, 0, 0, 0);
  const yesterdayEnd = new Date(yesterdayStart);
  yesterdayEnd.setHours(23, 59, 59, 999);
  const lastWeekStart = new Date(now);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);
  lastWeekStart.setHours(0, 0, 0, 0);
  const lastWeekEnd = new Date(lastWeekStart);
  lastWeekEnd.setHours(23, 59, 59, 999);

  const [today, yesterday, lastWeek] = await Promise.all([
    getScopedSalesAgg(branchId, todayStart.toISOString(), todayEnd.toISOString(), scope),
    getScopedSalesAgg(branchId, yesterdayStart.toISOString(), yesterdayEnd.toISOString(), scope),
    getScopedSalesAgg(branchId, lastWeekStart.toISOString(), lastWeekEnd.toISOString(), scope),
  ]);

  return {
    todayTotal: today.total,
    todayCount: today.count,
    yesterdayTotal: yesterday.total,
    yesterdayCount: yesterday.count,
    sameDayLastWeekTotal: lastWeek.total,
    sameDayLastWeekCount: lastWeek.count,
  };
};

export { CASHIER_LIST_FILTER_NULL } from './salesRepoSql';
